import React, { useMemo } from 'react';
import { Activity, TrendingUp } from 'lucide-react';

const SUBJECT_COLOURS = {
  Mathematics: 'bg-orange-500',
  Science: 'bg-blue-500',
  English: 'bg-emerald-500',
  'Social Studies': 'bg-purple-500',
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEKS_TO_SHOW = 12;

/**
 * Weekly Analytics — Topic Tests only.
 * Per-subject mini-bar charts showing the class's average score per ISO-week
 * across the last 12 weeks. Empty weeks render as a faint placeholder bar.
 */
const TeacherWeeklyAnalytics = ({ results = [], allowedSubjects = null }) => {
  // Build the last N weeks (Monday-anchored)
  const weeks = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    // Move to Monday of current week
    const day = now.getDay(); // 0=Sun..6=Sat
    const monOffset = day === 0 ? -6 : 1 - day;
    const currentMon = new Date(now);
    currentMon.setDate(now.getDate() + monOffset);
    const arr = [];
    for (let i = WEEKS_TO_SHOW - 1; i >= 0; i--) {
      const start = new Date(currentMon.getTime() - i * WEEK_MS);
      const end = new Date(start.getTime() + WEEK_MS);
      arr.push({
        start,
        end,
        label: `W${WEEKS_TO_SHOW - i}`,
        rangeLabel: `${start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${new Date(end.getTime() - 86400000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`,
      });
    }
    return arr;
  }, []);

  // Filter to Topic Tests only, then group by subject (respecting teacher's allowedSubjects)
  const bySubject = useMemo(() => {
    const allowedSet = Array.isArray(allowedSubjects) && allowedSubjects.length > 0
      ? new Set(allowedSubjects)
      : null;
    const topicResults = results.filter(r => (r.exam_type === 'topic_wise') || (r.category === 'topic_wise'));
    const groups = {};
    topicResults.forEach(r => {
      const subj = r.subject || 'Other';
      if (allowedSet && !allowedSet.has(subj)) return;
      if (!groups[subj]) groups[subj] = [];
      groups[subj].push(r);
    });
    return groups;
  }, [results, allowedSubjects]);

  const subjects = Object.keys(bySubject).sort();
  const totalAttempts = Object.values(bySubject).reduce((s, arr) => s + arr.length, 0);
  const totalAvg = totalAttempts > 0
    ? Math.round(
        Object.values(bySubject).reduce((s, arr) => s + arr.reduce((a, r) => a + (Number(r.score) || 0), 0), 0)
        / totalAttempts
      )
    : 0;

  // Build per-subject per-week averages
  const buildWeekly = (subj) => {
    const arr = bySubject[subj] || [];
    return weeks.map(w => {
      const inWeek = arr.filter(r => {
        const t = r.completed_at || r.submitted_at || r.created_at;
        if (!t) return false;
        const dt = new Date(t);
        return dt >= w.start && dt < w.end;
      });
      const count = inWeek.length;
      const avg = count > 0 ? Math.round(inWeek.reduce((s, r) => s + (Number(r.score) || 0), 0) / count) : 0;
      return { ...w, count, avg };
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="teacher-weekly-analytics">
      <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-blue-700 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={20} />
          <h3 className="font-semibold">Weekly Analytics — Topic Tests</h3>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <div>
            <span className="opacity-70">Topic Test attempts (12w)</span>{' '}
            <span className="font-bold text-base">{totalAttempts}</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp size={14} />
            <span className="opacity-70">Avg</span>{' '}
            <span className="font-bold text-base">{totalAvg}%</span>
          </div>
        </div>
      </div>

      {subjects.length === 0 ? (
        <p className="p-10 text-center text-gray-500 text-sm">
          No Topic Test attempts in the last {WEEKS_TO_SHOW} weeks. Once students start attempting Topic Tests, weekly averages per subject will appear here.
        </p>
      ) : (
        <div className="p-6 space-y-6">
          {subjects.map(subj => {
            const data = buildWeekly(subj);
            const totalSubjAttempts = data.reduce((s, d) => s + d.count, 0);
            const subjAvg = totalSubjAttempts > 0
              ? Math.round(data.reduce((s, d) => s + d.avg * d.count, 0) / totalSubjAttempts)
              : 0;
            const colour = SUBJECT_COLOURS[subj] || 'bg-gray-500';
            return (
              <div key={subj} data-testid={`weekly-subject-${subj}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-3 h-3 rounded-full ${colour}`} />
                    <h4 className="font-semibold text-gray-800">{subj}</h4>
                  </div>
                  <div className="text-xs text-gray-500">
                    {totalSubjAttempts} attempts · avg {subjAvg}%
                  </div>
                </div>
                <div className="flex items-end gap-1.5 h-32 border-b border-l border-gray-200 pl-2 pb-1">
                  {data.map((w, i) => {
                    const hasData = w.count > 0;
                    const h = hasData ? w.avg : 0; // bar height = avg percentage
                    return (
                      <div
                        key={i}
                        className="flex-1 flex flex-col items-center justify-end h-full group relative"
                        data-testid={`weekly-bar-${subj}-${w.label}`}
                      >
                        <div
                          className={`w-full rounded-t ${hasData ? colour : 'bg-gray-100'} transition-all`}
                          style={{ height: hasData ? `${Math.max(4, h)}%` : '6%', opacity: hasData ? 1 : 0.5 }}
                          title={`${w.label} (${w.rangeLabel}): ${w.count} attempts · avg ${w.avg}%`}
                        />
                        {/* hover tooltip */}
                        {hasData && (
                          <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                            {w.avg}% · {w.count} attempt{w.count === 1 ? '' : 's'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-1.5 mt-1 pl-2 text-[10px] text-gray-400">
                  {data.map((w, i) => (
                    <div key={i} className="flex-1 text-center">{w.label}</div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="text-[11px] text-gray-400 text-center pt-2 border-t">
            Bars show <strong>class average %</strong> per ISO-week (Mon–Sun). Hover for attempt count. Empty bars = no Topic Test attempts that week.
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherWeeklyAnalytics;
